import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import heroFlowers from '../images/flowersBrickWall2R.jpg';
import gardenView from '../images/newBackgroundResized.jpg';
import gardenToolsArt from '../images/homepage/garden-tools-watercolor-v2.png';
import weatherStationSunny from '../images/weatherStationSunny.jpg';
import { homepageImages } from './Images';
import ImageGallery from './ImageGallery';
import { upcomingEvents, pastEvents } from '../config/events';
import { WEATHER_STATION_ENABLED } from '../config/featureFlags';

const DEPLOY_CHECK_MESSAGE =
  "Emma's Court deploy check: 2026-05-04 pdf-rewrite-update is live";

function DateBlock({ month, day }) {
  const daySize = day.length > 2 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl';

  return (
    <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center border border-[#d5cfc0] bg-[#fffdf7] text-center sm:h-[4.5rem] sm:w-[4.5rem]">
      <span className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-[#a43b29]">
        {month}
      </span>
      <span className={`mt-1 font-serif leading-none text-[#1d2f24] ${daySize}`}>
        {day}
      </span>
    </div>
  );
}

function BotanicalSketch() {
  return (
    <div className="pointer-events-none hidden h-36 w-48 opacity-70 md:block" aria-hidden="true">
      <svg viewBox="0 0 180 132" className="h-full w-full text-[#314935]">
        <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2">
          <path d="M28 116 C34 82 36 52 29 22" />
          <path d="M31 77 C17 67 12 55 15 40 C31 44 39 54 35 70" />
          <path d="M34 56 C47 47 53 35 51 18 C36 22 27 36 31 51" />
          <path d="M74 116 C76 80 80 50 96 18" />
          <path d="M79 76 C64 66 60 52 66 39 C80 45 86 57 82 71" />
          <path d="M91 44 C108 43 120 34 125 18 C108 16 96 24 91 39" />
          <path d="M128 116 C126 88 132 58 154 26" />
          <path d="M132 80 C148 78 162 68 168 53 C151 50 139 61 134 74" />
          <path d="M144 48 C132 38 129 26 134 12 C147 17 154 29 151 43" />
          <circle cx="26" cy="20" r="7" />
          <circle cx="96" cy="17" r="8" />
          <circle cx="155" cy="24" r="7" />
        </g>
      </svg>
    </div>
  );
}

function HomePage() {
  useEffect(() => {
    console.info(DEPLOY_CHECK_MESSAGE);
  }, []);

  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto grid max-w-[92rem] grid-cols-1 lg:min-h-[640px] lg:grid-cols-[minmax(0,1.12fr)_minmax(15rem,0.36fr)_minmax(22rem,0.76fr)]">
          <div className="relative flex min-h-[23rem] border-b border-[#d8d0be] px-6 py-5 sm:min-h-[25rem] sm:px-10 sm:py-6 md:min-h-[31rem] md:py-8 lg:min-h-0 lg:border-b-0 lg:border-r lg:px-12 xl:px-14">
            <div
              className="absolute inset-y-0 hidden overflow-hidden md:block"
              style={{
                left: 'calc((100vw - min(100vw, 92rem)) / -2)',
                width: 'calc(21rem + (100vw - min(100vw, 92rem)) / 2)',
                WebkitMaskImage: 'linear-gradient(to right, #000 0%, #000 calc(100% - 11rem), rgba(0,0,0,0) calc(100% - 4rem), rgba(0,0,0,0) 100%)',
                maskImage: 'linear-gradient(to right, #000 0%, #000 calc(100% - 11rem), rgba(0,0,0,0) calc(100% - 4rem), rgba(0,0,0,0) 100%)',
              }}
            >
              <img
                src={gardenToolsArt}
                alt=""
                className="h-full w-full object-cover object-left mix-blend-multiply"
                aria-hidden="true"
              />
            </div>

            <div className="relative z-10 flex w-full select-none flex-col justify-start pt-10 sm:pt-12 md:pl-52 md:pt-20 lg:pl-56 lg:pt-20 xl:pt-24">
              <h1 className="max-w-3xl font-serif text-[4rem] font-normal leading-[0.86] tracking-normal text-[#0c351e] sm:text-[5.5rem] md:text-[7rem] lg:text-[5.8rem] xl:text-[6.5rem] 2xl:text-[7.8rem]">
                Emma's
                <span className="block">Court</span>
              </h1>
              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.42em] text-[#8e3a2b]">
                Community Garden
              </p>
              <div className="mt-7 h-px w-full max-w-md bg-[#304536]" />
              <p className="font-handwritten mt-6 max-w-lg text-xl leading-snug text-[#243c2c] sm:text-2xl lg:text-[1.65rem]">
                Grow food. Build community.
                <span className="block">Rooted in our neighborhood.</span>
              </p>
            </div>
          </div>

          <div className="relative hidden overflow-hidden border-b border-[#d8d0be] lg:block lg:border-b-0 lg:border-r">
            <img
              src={heroFlowers}
              alt="Flowers growing along a brick wall at Emma's Court"
              className="h-full w-full object-cover"
            />
          </div>

          <aside className="bg-[#fbf8ef] px-5 py-8 sm:px-8 lg:px-10 lg:py-12">
            <div className="flex items-end justify-between gap-4 border-b border-[#cfc7b6] pb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                  Events
                </p>
                <h2 className="mt-2 font-serif text-3xl text-[#10351f] sm:text-4xl">
                  Upcoming
                </h2>
              </div>
              <Link
                to="/events"
                className="hidden text-sm font-semibold uppercase tracking-[0.2em] text-[#43513f] hover:text-[#8e3a2b] sm:inline"
              >
                All events
              </Link>
            </div>

            <div className="divide-y divide-[#d7cfbd]">
              {upcomingEvents.map((event) => (
                <Link
                  key={event.id}
                  to={`/events#${event.id}`}
                  className="group grid grid-cols-[auto_1fr] gap-4 py-4 sm:gap-5 no-underline"
                >
                  <DateBlock month={event.month} day={event.day} />
                  <div className="min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-serif text-xl leading-tight text-[#18271e]">
                        {event.title}
                      </h3>
                      <span className="mt-1 text-xl leading-none text-[#a43b29] transition-transform group-hover:translate-x-1" aria-hidden="true">
                        &rarr;
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-[#4f5c50]">
                      {event.details}
                    </p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#70806f]">
                      {event.location}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {upcomingEvents.length === 0 && (
              <p className="mt-3 text-sm text-[#566556]">No planned events now, check back here for any new garden events.</p>
            )}

            {pastEvents.length > 0 && (
              <div className="mt-6">
                <h2 className="font-serif text-3xl text-[#10351f] sm:text-4xl">Past</h2>
                <div className="mt-3 divide-y divide-[#d7cfbd] border-y border-[#d7cfbd]">
                  {pastEvents.map((event) => (
                    <Link
                      key={event.id}
                      to={`/events#${event.id}`}
                      className="grid grid-cols-[auto_1fr] gap-4 py-3 no-underline sm:grid-cols-[auto_1fr_7.5rem] sm:items-center"
                    >
                      <DateBlock month={event.month} day={event.day} />
                      <h3 className="min-w-0 font-serif text-lg leading-tight text-[#18271e] sm:text-xl">
                        {event.title}
                      </h3>
                      {event.image && (
                        <img
                          src={event.image}
                          alt=""
                          className="col-span-2 h-24 w-full object-cover sm:col-span-1 sm:h-16"
                        />
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 border-t border-[#cfc7b6] pt-5">
              <Link
                to="/events"
                className="inline-flex w-full items-center justify-between border border-[#43513f] px-4 py-3 text-sm font-medium text-[#18271e] transition-colors hover:bg-[#10351f] hover:text-[#fbf8ef] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b]"
              >
                <span>View all events</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-b border-[#d8d0be] bg-[#f8f4ea]">
        <div className="mx-auto grid max-w-[92rem] gap-8 px-6 py-14 sm:px-10 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] md:items-end md:py-20 lg:px-16">
          <div className="overflow-hidden border border-[#d3cbbb]">
            <img
              src={gardenView}
              alt="Emma's Court garden beds in warm sunlight"
              className="aspect-[4/3] w-full object-cover md:aspect-[5/4] lg:aspect-[16/10]"
            />
          </div>

          <div className="relative border-t border-[#cfc7b6] pt-8 md:pt-10">
            <div className="flex items-start justify-between gap-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                  About the space
                </p>
                <h2 className="mt-4 max-w-xl font-serif text-4xl leading-tight text-[#10351f] sm:text-5xl">
                  A Garden For Neighbors
                </h2>
              </div>
              <BotanicalSketch />
            </div>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-[#445044]">
              Emma's Court is a space for people in the community to come together and grow food for their families, while building relationships with their neighbors. It's also a green space to connect with nature, and explore our creativity.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 border-t border-[#cfc7b6] pt-6">
              <Link
                to="/apply"
                className="inline-flex flex-1 items-center justify-between border border-[#43513f] bg-[#10351f] px-5 py-4 text-base font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#f8f4ea] sm:flex-none sm:w-56"
              >
                <span>Get Involved</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
              <Link
                to="/about"
                className="inline-flex flex-1 items-center justify-between border border-[#43513f] px-5 py-4 text-base font-medium text-[#18271e] transition-colors hover:bg-[#10351f] hover:text-[#fbf8ef] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#f8f4ea] sm:flex-none sm:w-56"
              >
                <span>Learn More</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {WEATHER_STATION_ENABLED && (
        <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
          <div className="mx-auto grid max-w-[92rem] gap-8 px-6 py-14 sm:px-10 md:py-20 lg:grid-cols-[minmax(0,0.94fr)_minmax(0,1.06fr)] lg:items-center lg:px-16">
            <div className="overflow-hidden border border-[#d3cbbb] bg-[#efe7d8]">
              <img
                src={weatherStationSunny}
                alt="Tempest weather station mounted above the historic garden wall under a blue sky"
                className="aspect-[4/3] w-full object-cover object-[center_38%] md:aspect-[5/4] lg:aspect-[16/12]"
              />
            </div>

            <div className="border-t border-[#cfc7b6] pt-8 md:pt-10">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Garden Projects
              </p>
              <h2 className="mt-4 max-w-xl font-serif text-4xl leading-tight text-[#10351f] sm:text-5xl">
                Weather Station
              </h2>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-[#445044]">
                Our solar-powered weather station helps us track conditions across the
                garden. Data is collected with a DIY radio transmitter, giving us a
                closer read on what the space is experiencing in real time. We also
                contribute these observations to NOAA's MADIS network as citizen scientists.
              </p>
              <div className="mt-9 flex flex-wrap gap-3 border-t border-[#cfc7b6] pt-6">
                <Link
                  to="/weather-station"
                  className="inline-flex flex-1 items-center justify-between border border-[#43513f] bg-[#10351f] px-5 py-4 text-base font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef] sm:flex-none sm:w-64"
                >
                  <span>View Weather Station</span>
                  <span aria-hidden="true">&rarr;</span>
                </Link>
                <a
                  href="https://madis-data.ncep.noaa.gov/MadisSurface/?CenterLAT=39.97&CenterLON=-75.14&Zoom=16.00&networks=ALL.ALL&StationID=G7606"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-between border border-[#43513f] px-5 py-4 text-base font-medium text-[#10351f] transition-colors hover:bg-[#efe7d8] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef] sm:flex-none sm:w-64"
                >
                  <span>View NOAA Station G7606</span>
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#f1eadc] px-4 py-14 sm:px-6 md:py-16">
        <div className="mx-auto max-w-[92rem]">
          <div className="mb-7">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
              In the garden
            </p>
            <h2 className="mt-3 font-serif text-3xl text-[#10351f] sm:text-4xl">
              Recent Views
            </h2>
          </div>
          <ImageGallery images={homepageImages} />
          <div className="mt-8 flex justify-center">
            <Link
              to="/gallery"
              className="inline-flex min-w-56 items-center justify-between gap-8 border border-[#43513f] px-5 py-4 text-base font-medium text-[#10351f] transition-colors hover:bg-[#10351f] hover:text-[#fbf8ef] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#f1eadc]"
            >
              <span>View the full gallery</span>
              <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
